<template>
  <div class="product-details-container">
    <a-spin :spinning="loading">
      <DetailsHeader :breadcrumbItems="productDetailsBreadcrumbs">
        <div class="header-content">
          <a-avatar
            size="large"
            :style="{
              backgroundColor: '#d1fae5',
              verticalAlign: 'middle',
              color: '#047857',
            }"
            :gap="4"
            :alt="seller.businessDetails.businessName"
            class="avatar"
          >
            {{ getInitials(seller.businessDetails.businessName) }}
          </a-avatar>

          <div class="seller-info">
            <h2>{{ seller.businessDetails.businessName || "" }}</h2>
            <div style="display: flex; align-items: center; gap: 8px">
              <p>{{ seller.businessDetails.businessType || "N/A" }}</p>
              :
              <p>{{ seller.accountDetails.operationZone || "N/A" }}</p>
            </div>
            <StatusTag :status="seller.accountDetails.sellerApprovalStatus" />
          </div>
        </div>
      </DetailsHeader>

      <a-row :gutter="[24, 24]">
        <a-col :span="12">
          <ProductDetailsCard
            title="Global Product Details"
            :product="product"
            :fields="globalProductFields"
          />
        </a-col>
        <a-col :span="12">
          <ProductDetailsCard
            title="Seller-Specific Details"
            :product="product"
            :fields="sellerSpecificFields"
          />
        </a-col>
      </a-row>

      <a-row :gutter="[24, 24]">
        <a-col :span="12">
          <ProductDetailsCard
            title="Global Product Details"
            :product="product"
            :fields="productFields"
          />
        </a-col>
        <a-col :span="12">
          <ProductDetailsCard
            title="Profit & Regional Breakdown"
            :product="product"
            :fields="profitRegionalFields"
          />
        </a-col>
      </a-row>

      <a-button @click="goBack" class="back-button" :bordered="false">
        <a-icon type="arrow-left" /> Back to Product Catalog
      </a-button>
    </a-spin>
  </div>
</template>

<script>
import DetailsHeader from "../DetailsHeader.vue";
import StatusTag from "../../shared-components/StatusTag.vue";
import ProductDetailsCard from "./ProductDetailsCard.vue";
import { formatAmount, formatDate, getInitials } from "../../../utils";
import { mapState, mapActions } from "vuex";
import sellersService from "../../../api/sellers.service";

export default {
  name: "ProductDetails",
  components: {
    DetailsHeader,
    ProductDetailsCard,
    StatusTag,
  },
  props: {
    productId: {
      type: String,
      required: true,
    },
    userSetupId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      product: {},
      loading: false,
      // sellerData will be set from the store's seller state in computed
      globalProductFields: [
        { key: "productCatalog.productName", label: "Name" },
        { key: "productCatalog.productGroup", label: "Product Group" },
        { key: "productCatalog.category", label: "Category" },
        { key: "productCatalog.manufacturer", label: "Manufacturer" },
        { key: "productCatalog.countryOfOrigin", label: "Country of Origin" },
        { key: "productCatalog.targetSpecies", label: "Target Species" },
        { key: "productCatalog.diseasePrevented", label: "Diseases Prevented" },
      ],
      sellerSpecificFields: [
        {
          key: "costPrice",
          label: "Cost Price",
          formatter: (value) => this.formatAmount(value),
        },
        {
          key: "sellingPrice",
          label: "Custom Selling Price",
          formatter: (value) => this.formatAmount(value),
        },
        {
          key: "profitMargin",
          label: "Gross Margin %",
          formatter: (value) => (value ? `${value}%` : "N/A"),
        },
        {
          key: "onHandStock",
          label: "On-Hand Stock",
          formatter: (value) => (value ? `${value} units` : "N/A"),
        },
        {
          key: "unitsSold30d",
          label: "Units Sold (30d)",
          formatter: (value) => (value ? `${value} units` : "N/A"),
        },
        {
          key: "daysOfInventory",
          label: "Days of Inventory",
          formatter: (value) => (value ? `${value} days` : "N/A"),
        },
      ],
      productFields: [
        {
          key: "salesVelocity",
          label: "Sales Velocity",
          formatter: (value) =>
            value ? `${value} units/day (30d avg)` : "N/A",
        },
        {
          key: "inventoryTurnoverRatio",
          label: "Inventory Turnover Ratio",
          formatter: (value) => (value ? `${value} (30d)` : "N/A"),
        },
        {
          key: "returnRate",
          label: "Return Rate %",
          formatter: (value) => (value ? `${value}%` : "N/A"),
        },
        {
          key: "expiryRisk",
          label: "Expiry Risk",
        },
      ],
      profitRegionalFields: [
        {
          key: "revenue30d",
          label: "Revenue (30d)",
          formatter: (value) => this.formatAmount(value),
        },
        {
          key: "cost30d",
          label: "Cost (30d)",
          formatter: (value) => this.formatAmount(value),
        },
        {
          key: "grossProfit30d",
          label: "Gross Profit (30d)",
          formatter: (value) => this.formatAmount(value),
        },
        {
          key: "regionalSales",
          label: "Regional Sales",
          formatter: (value) => {
            if (!value) return "N/A";
            return Object.entries(value)
              .map(
                ([region, amount]) => `${region}: ${this.formatAmount(amount)}`
              )
              .join(", ");
          },
        },
        {
          key: "seasonalityTrend",
          label: "Seasonality Trend (Monthly Sales, 12mo)",
          formatter: () => "", // Return empty string to make it descriptive without a value
        },
      ],
    };
  },
  computed: {
    ...mapState("sellerManagement", ["seller"]),
    productDetailsBreadcrumbs() {
      return [
        {
          text: "Seller Details",
          to: `/sellers/${this.userSetupId}`,
        },
        {
          text: "Product Details",
          to: null, // Last item, no navigation
        },
      ];
    },
  },
  async created() {
    const response = await this.fetchSeller(this.userSetupId);
    if (response?.data?.code !== 200) {
      this.$message.warning(
        "An error occured seller details, please try again"
      );
    }
    await this.fetchProductDetails(this.productId);
  },
  methods: {
    getInitials,
    formatAmount,
    formatDate,
    ...mapActions("sellerManagement", ["fetchSeller"]),
    async fetchProductDetails(sellerProductCatalogId) {
      this.loading = true;
      const response = await sellersService.getProductDetails(
        sellerProductCatalogId,
        this.userSetupId
      );
      if (response.data.code === 200) {
        this.product = response.data.data;
      } else {
        this.$message.warning("An error occurred while retriving product");
        this.product = {}; // Clear product data if not found
      }
      this.loading = false;
    },
    goBack() {
      if (this.userSetupId) {
        this.$router.push(`/sellers/${this.userSetupId}?tab=4`);
      } else {
        this.$router.go(-1); // Go back one step in history if sellerId is not available
      }
    },
  },
};
</script>

<style scoped>
.product-details-container {
  padding: 24px;
  background-color: #f0f2f5;
  min-height: calc(
    100vh - 84px
  ); /* Adjust based on your header/footer height */
}

.product-card {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
  padding: 24px;
}

.product-title {
  font-size: 18px;
  font-weight: 800;
  color: #333;
  margin: 0;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.product-card-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.product-card-header {
  color: #6b7280;
  font-size: 16px;
  font-weight: 400;
}

.product-card-paragraph {
  color: #111827;
  font-weight: 500;
  font-size: 16px;
}

.back-button {
  background-color: #26a246;
  color: #fafafa;
  border-radius: 6px;
}

.detail-group {
  margin-bottom: 16px;
}

.detail-label {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 4px;
  font-weight: 500;
}

.detail-value {
  font-size: 16px;
  color: #333;
  font-weight: 600;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
}

/* Ant Design overrides for consistent styling */
.ant-card > .ant-card-head {
  font-weight: 800 !important;
  padding: 16px 24px;
  border-bottom: none !important;
  margin-bottom: 20px !important;
}

.ant-card-head-title {
  padding: 0 !important;
  font-weight: 800 !important;
  border-bottom: none !important;
}

.ant-card {
  margin-bottom: 20px !important;
}

.ant-card-body {
  padding: 24px !important;
}

.ant-tag {
  border-radius: 4px;
  font-weight: 600;
}
</style>
